import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { LocaleSwitcher } from "../i18n/LocaleSwitcher";
import { GlobalSearch } from "./GlobalSearch";
import { NotificationBell } from "./NotificationBell";
import { ThemeToggle } from "./ThemeToggle";
import { AssistantChat } from "./assistant/AssistantChat";
import { ChatContext, useChatConnection } from "./chat/useChatConnection";
import { tmApi } from "../api/tm";
import Ic from "./icons.jsx";
import type { ComponentType, ReactNode } from "react";

type NavEntry = {
  to: string;
  label: string;
  icon: ComponentType<Record<string, unknown>>;
  permission?: string;
  /** Force-hide regardless of permission (e.g. a customer with Forecasts disabled). */
  hidden?: boolean;
  anyOf?: string[];
  role?: string;
  /** Show only if the caller holds at least one of these roles. */
  anyOfRoles?: string[];
  /** Require exact match for active state. Use for a parent route whose
   * prefix would otherwise highlight every child route alongside the leaf. */
  end?: boolean;
  /** Renders as a non-navigable "coming soon" row instead of a link — the
   * route doesn't exist yet. */
  disabled?: boolean;
  /** Optional numeric badge shown next to the label (e.g. open task count). */
  badge?: number;
  /** Optional subheading this item sits under, inside its group. Items that share
   * a section render under one subheading; a section whose items are all filtered
   * away by permissions disappears with them. Only groups large enough to need it
   * use sections — see the comment on the quality group. */
  section?: string;
};

type NavGroup = {
  id: string;
  title: string;
  items: NavEntry[];
};

const FOLDED_NAV_KEY = "bankom.nav.folded";

/** Which sidebar groups the person has folded away. Kept in the browser, per person,
 * so the sidebar opens the way they left it. Every read and write is guarded: in a
 * private window, with site data cleared, or with storage blocked, the accessor
 * throws — and the menu must still render, open. */
function useFoldedNavGroups(pathname: string) {
  // Koje grupe nose otvoreni ekran. Puni se pri iscrtavanju, a cita u ucinku ispod — ucinci se
  // izvrsavaju posle iscrtavanja, pa je do tada tacno.
  const aktivne = useRef<Set<string>>(new Set());
  const [folded, setFolded] = useState<string[]>(() => {
    try {
      const raw = window.localStorage.getItem(FOLDED_NAV_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
    } catch {
      return [];
    }
  });

  function toggle(id: string) {
    setFolded((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try {
        window.localStorage.setItem(FOLDED_NAV_KEY, JSON.stringify(next));
      } catch {
        // Storage refused. The fold still applies for this visit; it just is not remembered.
      }
      return next;
    });
  }

  // Kad se predje na ekran iz sklopljene grupe, ta grupa se otklapa — jednom, pri promeni rute.
  // Ranije je bila PRINUDNO otvorena dok je ekran u njoj, pa je njeno zaglavlje bilo mrtvo dugme:
  // klik nista ne bi promenio, a izbor bi se tiho primenio kasnije, na drugoj ruti.
  useEffect(() => {
    setFolded((prev) => (prev.some((id) => aktivne.current.has(id)) ? prev.filter((id) => !aktivne.current.has(id)) : prev));
  }, [pathname]);

  function otvoriSve() {
    setFolded([]);
    try {
      window.localStorage.setItem(FOLDED_NAV_KEY, "[]");
    } catch {
      // Storage refused; the menu is open for this visit either way.
    }
  }

  return {
    isFolded: (id: string) => folded.includes(id),
    koliko: folded.length,
    otvoriSve,
    toggle,
    /** Prijavi koje grupe nose otvoreni ekran; cita se tek u ucinku iznad. */
    zapamtiAktivne: (ids: Set<string>) => {
      aktivne.current = ids;
    },
  };
}

export function AppShell() {
  const { t } = useTranslation();
  const { state, logout, hasPermission, hasRole } = useAuth();
  const navigate = useNavigate();

  // Count of open tasks assigned to me — feeds the Tasks sidebar badge.
  // Refreshed when the auth state flips and every 60 s so the badge stays
  // roughly fresh without a websocket.
  const [openTaskCount, setOpenTaskCount] = useState(0);
  useEffect(() => {
    if (state.status !== "authenticated" || !hasPermission("section:tm_tasks:read")) return;
    let cancelled = false;
    async function refresh() {
      if (document.hidden) return; // no background polling — see NotificationBell
      try {
        const tasks = await tmApi.mine();
        if (!cancelled) setOpenTaskCount(tasks.length);
      } catch {
        if (!cancelled) setOpenTaskCount(0);
      }
    }
    void refresh();
    const id = setInterval(refresh, 60_000);
    const onVisible = () => { void refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { cancelled = true; clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
  }, [state.status, hasPermission]);

  // The staff chat: its stream stays open on every screen, so the menu's unread count is current wherever one is.
  const chat = useChatConnection(
    state.status === "authenticated" && hasPermission("section:chat:read"),
    state.status === "authenticated" ? state.user.id : "",
  );

  // Collapsed sidebar (icon-only) — remembered across sessions.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("bankom.navCollapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("bankom.navCollapsed", collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  // Mobile off-canvas nav — auto-closes on every route change (a nav item was tapped).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  // The desktop icon-collapse is meaningless on mobile (we use a drawer), so never apply it there.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 820px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 820px)");
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Koja grupa je sklopljena. Izbor je korisnikov i pamti se po pregledacu; kad se ne moze procitati
  // (privatan prozor, obrisani podaci) meni se otvara ceo, sto je ispravno zatecno stanje.
  // Stoji IZNAD ranog izlaza: kuka se poziva na svakom iscrtavanju ili React gubi redosled kuka.
  const foldedGroups = useFoldedNavGroups(location.pathname);

  if (state.status !== "authenticated") return null;

  // Portal side: `companyKind` is the source of truth once
  // present. Customer has a role-prefix fallback for the (unlikely) case a
  // JWT predates the claim; supplier has none.
  const isCustomer = state.user.companyKind != null
    ? state.user.companyKind === "CUSTOMER" || state.user.companyKind === "BOTH"
    : state.user.roles.some((r) => r.startsWith("CUST_"));
  const isInternal = state.user.internal;

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const groups: NavGroup[] = [
    {
      id: "overview",
      title: t("nav.group.overview", "Overview"),
      items: [
        { to: "/dashboard", label: t("nav.dashboard"), icon: Ic.dashboard },
        // Right under the dashboard: the staff open it many times a day, and its count belongs where it is seen.
        { to: "/chat", label: t("nav.chat", "Chat"), icon: Ic.chat, permission: "section:chat:read", badge: chat.unread },
      ],
    },
    {
      id: "products",
      title: t("nav.group.products", "Products"),
      items: [
        { to: "/catalog", label: t("nav.catalog"), icon: Ic.catalog, permission: "section:catalog:read" },
        // Uz katalog, jer je to isto pitanje sa druge strane: šta prodajemo, i koliko toga stvarno ima na stanju.
        { to: "/warehouse", label: t("nav.warehouse", "Magacin"), icon: Ic.box, permission: "section:warehouse:read" },
      ],
    },
    {
      id: "commercial",
      title: t("nav.group.commercial", "Commercial"),
      items: [
        // The company register follows effective read access and server-side data scope.
        // Mesečni izveštaj menadžmentu stoji prvi u sektoru: on je ono zbog čega se ostatak meri.
        { to: "/reports/sales", label: t("nav.managementReport", "Izveštaj komercijale"), icon: Ic.trend, permission: "section:management_report:read" },
        { to: "/markets", label: t("nav.markets.overview", "Markets"), icon: Ic.world, permission: "section:market_overview:read" },
        { to: "/admin/companies", label: t("nav.customers", "Companies"), icon: Ic.factory, permission: "section:companies:read" },
        // Distinct icons per item so Orders/Offers/Leads don't read as identical
        // rows: cart (orders), document (offers), person (leads).
        { to: "/orders", label: t("nav.orders", "Orders"), icon: Ic.commercial, permission: "section:orders:read" },
        { to: "/offers", label: t("nav.offers", "Offers"), icon: Ic.pdf, permission: "section:offers:read" },
        { to: "/leads", label: t("nav.leads", "Leads"), icon: Ic.user, permission: "section:leads:read" },
        // Nalog za otpremu je prodajni dokument: prodaja ga otvara iz porudzbine i salje kvalitetu.
        // Stajao je pod otkupom, sto je bila greska u razvrstavanju.
        {
          to: "/sales/dispatch-orders",
          label: t("nav.sales.dispatchOrders", "Nalozi za otpremu"),
          icon: Ic.truck,
          permission: "section:dispatch_orders:read",
        },
        // One nav item for both sides of the portal — complaints:read decides
        // visibility (CUST_* own company, SALES/SALES_DIRECTOR/KVALITET/ADMIN
        // per assignment and effective access.
        { to: "/complaints", label: t("nav.complaints", "Complaints"), icon: Ic.alert, anyOf: ["section:complaint_submission:read", "section:complaint_processing:read", "section:complaint_decisions:read", "section:complaint_team_8d:read", "section:complaint_outcome:read"] },
        // Next to complaints on purpose: a complaint names a lot, and this is where that
        // lot is traced — back to the raw materials and forward to every customer who got
        // goods from it. Internal only (the forward view exposes other customers).
        { to: "/traceability", label: t("nav.traceability", "Traceability"), icon: Ic.search, permission: "section:traceability:read" },
        // Finance sits high in Commercial — for customers it lands 3rd (Orders, Offers, Finance).
        { to: "/finance", label: t("finance.nav", "Finance"), icon: Ic.finance, permission: "section:customer_finance:read" },
        { to: "/forecasts", label: t("nav.forecasts", "Forecasts"), icon: Ic.trend, permission: "section:forecasts:read", hidden: isCustomer && !state.user.forecastsEnabled },
        {
          // Customers land on the read-only list; sales / admin land on the
          // richer admin page that also has the "New contract" CTA.
          to: hasPermission("section:sales_contracts:write") ? "/admin/contracts" : "/contracts",
          label: isCustomer ? t("nav.contract", "Contract") : t("nav.contracts", "Contracts"),
          icon: Ic.contract,
          permission: "section:sales_contracts:read",
        },
      ],
    },
    {
      // Supplier-facing side: procurement contracts (ugovori o otkupu) and
      // deliveries (isporuke i kvalitet). This section is visible to supplier +
      // BOTH companies and to the internal buy-in staff (Otkup/Kvalitet/Admin).
      id: "otkup",
      title: t("nav.group.otkup", "Otkup"),
      items: [
        {
          // Otkup/Admin land on the admin page with the "New contract" CTA;
          // supplier users land on the read-only list.
          to: hasPermission("section:procurement_contracts:write") ? "/admin/procurement/contracts" : "/procurement/contracts",
          label: t("nav.otkup.contracts", "Procurement contracts"),
          icon: Ic.contract,
          permission: "section:procurement_contracts:read",
        },
        {
          // Predlozi iz evidencije otkupa. Stoje odmah pod ugovorima, jer je to posao koji vodi do
          // ugovora: predlog se potvrdi i postane nacrt.
          to: "/procurement/imports",
          label: t("nav.otkup.imports", "Predlozi ugovora"),
          icon: Ic.contract,
          permission: "section:procurement_imports:read",
        },
        {
          to: "/procurement/deliveries",
          label: t("nav.otkup.deliveries", "Deliveries"),
          icon: Ic.truck,
          anyOf: ["section:delivery_intake:read", "section:delivery_quality:read", "section:delivery_refusal:read", "section:delivery_documents:read"],
        },
        {
          to: "/procurement/settlements",
          label: t("nav.otkup.settlements", "Settlement notes"),
          icon: Ic.contract,
          permission: "section:supplier_settlements:read",
        },
        {
          to: "/procurement/lots",
          label: t("nav.otkup.lots", "Intake lots"),
          icon: Ic.box,
          permission: "section:intake_lots:read",
        },
        {
          to: "/admin/settlement/rules",
          label: t("nav.otkup.settlementRules", "Settlement rules"),
          icon: Ic.finance,
          permission: "section:settlement_rules:read",
        },
        {
          to: "/procurement/finance",
          label: t("nav.otkup.finance", "Supplier finance"),
          icon: Ic.finance,
          permission: "section:supplier_finance:read",
        },
      ],
    },
    {
      // Kvalitet stoji sam. Dok je laboratorija merila samo ulaznu sirovinu, ovi ekrani su mogli da
      // vise pod otkupom; od kada se meri i u proizvodnji, na gotovom proizvodu i pri otpremi, to je
      // sektor koji dodiruje ceo lanac i ne pripada ni otkupu ni prodaji.
      //
      // JEDNA GRUPA, TRI PODNASLOVA — i to je ispravka od 08.10.2026. Kvalitet je tri razlicita
      // posla i trinaest stavki, pa podela ima smisla: pomesani, trinaest stavki traze od laboranta
      // da svaki dan prelazi preko sifarnika koje nikad ne otvara. Ali tri ODVOJENE grupe su bile
      // greska: zaglavlja su ispadala kao gola „Rad", „Izvestaji", „Sifarnici", bez reci koja kaze
      // cije su, pa je korisnik video tri bezimena odeljka umesto jednog „Kvalitet". Podnaslov nosi
      // podelu, nadnaslov nosi ime sektora.
      id: "kvalitet",
      title: t("nav.group.quality", "Quality"),
      items: [
        // Redosled prati tok posla: meri se, potpisuje, dopunjuje spoljnim nalazom, cuva se tegla,
        // prilazu se dokumenti, i na kraju LOT izlazi iz fabrike.
        // Radni red stoji PRE unosa nalaza, jer je to prvi potez smene: sta danas treba izmeriti.
        // Do njega se to saznavalo iz Viber grupe (KK28), a svaki drugi ekran modula trazi da oznaka
        // sarze vec bude poznata.
        // Proizvodni magacin bez statusa: LOT-ovi iz realizacije koje kvalitet još nije proglasio usaglašenim,
        // SS ili neusaglašenim. Vodi u Magacin sa tim filterom, jer je to ista roba, samo viđena iz kvaliteta.
        // Radni spisak kvaliteta, pa ga nosi pravo na nalaze: prodaja iste statuse vidi u Magacinu, a u njenom
        // meniju bi otvarao grupu Kvalitet sa jednom stavkom.
        {
          to: "/quality/unprocessed-lots",
          label: t("nav.quality.unprocessedLots", "Neobrađeni LOT-ovi"),
          icon: Ic.box,
          permission: "section:quality_readings:read",
          section: t("nav.sub.quality.work", "Work"),
        },
        {
          to: "/quality/queue",
          label: t("nav.quality.queue", "Za obradu"),
          icon: Ic.list,
          permission: "section:quality_readings:read",
          section: t("nav.sub.quality.work", "Work"),
        },
        {
          to: "/quality/readings",
          label: t("nav.quality.readings", "Nalazi na LOT-u"),
          icon: Ic.check,
          permission: "section:quality_readings:read",
          section: t("nav.sub.quality.work", "Work"),
        },
        // Red nalaza koji cekaju potpis stoji uz ekran nalaza, jer je to isti posao gledan sa
        // druge strane: tamo se unosi, ovde se potpisuje. Čitalac vidi red, a samo ovlašćeni
        // potpisnik vidi akciju potpisa.
        {
          to: "/quality/awaiting-verification",
          label: t("nav.quality.pendingVerification", "Čeka potpis"),
          icon: Ic.alert,
          permission: "section:quality_readings:read",
          section: t("nav.sub.quality.work", "Work"),
        },
        // Nalaz spoljne laboratorije unosi i laborant - to je posao koji on radi svaki put kad analiza
        // ide van kuce. Sam spisak laboratorija vodi direktor kvaliteta i stoji pod podesavanjem.
        {
          to: "/quality/external-analyses",
          label: t("nav.quality.externalAnalyses", "Spoljne analize"),
          icon: Ic.pdf,
          permission: "section:external_analyses:read",
          section: t("nav.sub.quality.work", "Work"),
        },
        // Kontrauzorak je registar tegli, ne nalaza: laborant ga vodi, otkup i prodaja ga citaju.
        {
          to: "/quality/retained-samples",
          label: t("nav.quality.retainedSamples", "Kontrauzorci"),
          icon: Ic.box,
          permission: "section:retained_samples:read",
          section: t("nav.sub.quality.work", "Work"),
        },
        // Cita ga i direktor prodaje, jer iz njega sastavlja odgovor kupcu na reklamaciju; prilaganje
        // i pustanje su druge dve dozvole i ekran sam skriva dugmad bez njih.
        {
          to: "/quality/lot-documents",
          label: t("nav.quality.lotDocuments", "Dokumenti uz LOT"),
          icon: Ic.pdf,
          permission: "section:lot_documents:read",
          section: t("nav.sub.quality.work", "Work"),
        },
        {
          to: "/quality/dispatch-release",
          label: t("nav.quality.dispatchRelease", "Puštanje LOT-a za otpremu"),
          icon: Ic.truck,
          permission: "section:dispatch_quality_release:read",
          section: t("nav.sub.quality.work", "Work"),
        },
        // Ten years of findings from Bankom's own lab program, read only. Jedan pregled, i to je
        // tacno stanje stvari — podnaslov postoji da se to vidi, umesto da stoji zamesan medju
        // radnim ekranima.
        {
          to: "/quality/lab-archive",
          label: t("nav.quality.labArchive", "Arhiv laboratorije"),
          icon: Ic.search,
          permission: "section:lab_archive:read",
          section: t("nav.sub.quality.reports", "Reports"),
        },
        // Sifarnici kvaliteta: ko ih vodi, taj ih i otvara. Laborant drzi test_plans:view jer forma za
        // unos iz plana gradi svoja polja, ali sam ekran plana mu ne treba - on dobija polja, ne opise.
        // Redosled ide od onoga sto se meri, preko onoga sto je propisano, do mesta i sifarnika.
        {
          to: "/admin/quality/parameters",
          label: t("nav.quality.parameters", "Katalog testova"),
          icon: Ic.tools,
          permission: "section:quality_parameters:read",
          section: t("nav.sub.quality.setup", "Master data"),
        },
        {
          to: "/admin/quality/test-plans",
          label: t("nav.quality.testPlans", "Testovi i plan kontrolisanja"),
          icon: Ic.tools,
          permission: "section:test_plans:read",
          section: t("nav.sub.quality.setup", "Master data"),
        },
        {
          to: "/quality/control-points",
          label: t("nav.quality.controlPoints", "Kontrolne tačke"),
          icon: Ic.tools,
          permission: "section:control_points:read",
          section: t("nav.sub.quality.setup", "Master data"),
        },
        {
          to: "/quality/production-steps",
          label: t("nav.quality.productionSteps", "Koraci proizvodnje"),
          icon: Ic.tools,
          permission: "section:production_steps:read",
          section: t("nav.sub.quality.setup", "Master data"),
        },
        // Klasa odlucuje u koji silos roba ide i u koje proizvode sme, pa stoji uz sifarnike.
        {
          to: "/admin/quality/classes",
          label: t("nav.quality.classes", "Klase sirovine"),
          icon: Ic.tools,
          permission: "section:quality_classes:read",
          section: t("nav.sub.quality.setup", "Master data"),
        },
        {
          to: "/quality/external-laboratories",
          label: t("nav.quality.externalLabs", "Spoljne laboratorije"),
          icon: Ic.tools,
          permission: "section:external_labs:read",
          section: t("nav.sub.quality.setup", "Master data"),
        },
      ],
    },
    {
      // Transport Manager — trips board, gate
      // and the carriers/vehicles/drivers admin screen.
      id: "transport",
      title: t("nav.group.transport", "Transport"),
      items: [
        { to: "/transport/trips", label: t("nav.transport.trips", "Trips"), icon: Ic.truck, anyOf: ["section:transport_inbound:read", "section:transport_outbound:read"] },
        { to: "/transport/gate", label: t("nav.transport.gate", "Gate"), icon: Ic.check, permission: "section:transport_gate:read" },
        { to: "/admin/transport/carriers", label: t("nav.transport.carriers", "Carriers"), icon: Ic.tools, permission: "section:carriers:read" },
      ],
    },
    {
      id: "resources",
      title: t("nav.group.resources", "Resources"),
      items: [
        { to: "/materials", label: t("nav.materials"), icon: Ic.materials, permission: "section:materials:read" },
        { to: "/news", label: t("nav.news"), icon: Ic.news, permission: "section:news:read" },
      ],
    },
    {
      id: "tm",
      title: t("nav.group.tm", "Task Manager"),
      items: [
        { to: "/tm",          label: t("nav.tm.overview", "Overview"), icon: Ic.dashboard, permission: "section:tm_activity:read", end: true },
        { to: "/tm/tasks",    label: t("nav.tm.tasks", "Tasks"),       icon: Ic.check,    permission: "section:tm_tasks:read", badge: openTaskCount },
        { to: "/tm/projects", label: t("nav.tm.projects", "Projects"), icon: Ic.book,     permission: "section:tm_projects:read" },
        { to: "/tm/meetings", label: t("nav.tm.meetings", "Meetings"), icon: Ic.calendar, permission: "section:tm_meetings:read" },
        { to: "/tm/team",     label: t("nav.tm.team", "Team"),         icon: Ic.user,     permission: "section:tm_teams:read" },
        { to: "/tm/activity", label: t("nav.tm.activity", "Activity"), icon: Ic.list,     permission: "section:tm_activity:read" },
      ],
    },
    {
      // Personal settings for internal staff. Only one entry today ("Moj mejl",
      // the caller's own mailbox), and its label lives under mail.* because the
      // whole module is internal-only (EN+SR) — a nav.* key would have to be
      // translated into all ten customer locales for no reader.
      id: "settings",
      title: t("mail.nav.group", "Settings"),
      items: [
        { to: "/settings/mail", label: t("mail.nav.settings", "My email"), icon: Ic.send, permission: "section:personal_mail:read" },
      ],
    },
    {
      id: "myCompany",
      title: t("nav.group.myCompany", "My company"),
      items: [
        { to: "/admin/users", label: t("nav.myTeam", "My team"), icon: Ic.user, permission: "section:users:read", hidden: isInternal },
        {
          to: "/contact",
          label: t("nav.contact", "Contact"),
          icon: Ic.handshake,
          anyOfRoles: ["CUST_MANAGER", "CUST_PROCUREMENT"],
        },
      ],
    },
    {
      id: "admin",
      title: t("nav.group.admin", "Admin"),
      items: [
        // Companies/Customers moved up to Commercial — keep this group
        // focused on admin-only surfaces (users, catalog, news, …).
        { to: "/admin/markets", label: t("nav.markets", "Markets"), icon: Ic.world, permission: "section:market_settings:read" },
        { to: "/admin/offer-stages", label: t("nav.offerStages", "Offer stages"), icon: Ic.commercial, permission: "section:offer_stages:read" },
        { to: "/admin/kpi", label: t("nav.kpi", "KPI targets"), icon: Ic.trend, permission: "section:kpi:read" },
        { to: "/admin/users", label: t("nav.users"), icon: Ic.user, permission: "section:users:read", hidden: !isInternal },
        { to: "/admin/permissions", label: t("nav.permissions", "Access permissions"), icon: Ic.tools, permission: "section:access_control:read" },
        { to: "/admin/catalog/products", label: t("nav.adminCatalog", "Catalog"), icon: Ic.catalog, permission: "section:catalog:read", hidden: !isInternal },
        { to: "/admin/materials", label: t("nav.adminMaterials", "Materials"), icon: Ic.materials, permission: "section:materials:read", hidden: !isInternal },
        { to: "/admin/news", label: t("nav.adminNews", "News"), icon: Ic.news, permission: "section:news:read", hidden: !isInternal },
        { to: "/admin/contracts", label: t("nav.adminContracts", "Contracts"), icon: Ic.contract, permission: "section:sales_contracts:read", hidden: !isInternal },
        { to: "/admin/document-templates", label: t("nav.adminDocumentTemplates", "Document templates"), icon: Ic.copy, permission: "section:document_templates:read", hidden: !isInternal },
        { to: "/admin/orders", label: t("nav.adminOrders", "Order review"), icon: Ic.commercial, permission: "section:orders:read", hidden: !isInternal },
        { to: "/admin/erp", label: t("nav.adminErp", "ERP outbox"), icon: Ic.refresh, permission: "section:erp_operations:read", hidden: !isInternal },
        { to: "/admin/exchange-rates", label: t("nav.adminExchangeRates", "Exchange rates"), icon: Ic.finance, permission: "section:exchange_rates:read", hidden: !isInternal },
        { to: "/admin/audit-log", label: t("nav.adminAudit", "Audit log"), icon: Ic.book, permission: "section:audit:read" },
        { to: "/admin/emails/preview", label: t("nav.adminEmails", "Email preview"), icon: Ic.send, permission: "section:email_templates:read", hidden: !isInternal },
        {
          to: "/admin/security/jwt-keys",
          label: t("nav.adminJwtKeys", "Ključevi prijava"),
          icon: Ic.cog,
          permission: "section:security_keys:read",
        },
      ],
    },
  ];

  const visibleGroups = groups
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => {
        if (it.hidden) return false;
        if (it.anyOf) return it.anyOf.some((p) => hasPermission(p));
        if (it.permission) return hasPermission(it.permission);
        if (it.role) return hasRole(it.role) || hasRole("SUPERADMIN");
        if (it.anyOfRoles) return it.anyOfRoles.some((r) => hasRole(r));
        return true;
      }),
    }))
    .filter((g) => g.items.length > 0);

  // Uski meni je SAMO IKONE, i to je `collapsed` uz uslov da nije telefon — na telefonu je fioka sa
  // punim natpisima. Racuna se jednom i koristi i u klasi i u uslovu sklapanja: dva razlicita izraza
  // nad istim stanjem dala su mrtvo dugme u fioci, koje je tiho upisivalo izbor a nista nije menjalo.
  const samoIkone = collapsed && !isMobile;

  // Grupa u kojoj je otvoreni ekran stoji otvorena bez obzira na izbor — inace bi korisnik gledao
  // sklopljen meni a da ne vidi gde se nalazi. Jedna putanja ume da stoji u DVE grupe
  // (`/admin/users` je i u podesavanjima i u administraciji), pa se otvaraju obe: birati jednu znaci
  // otvoriti onu iz koje korisnik nije kliknuo.
  const activeGroupIds = new Set(
    visibleGroups
      .filter((g) =>
        g.items.some((it) => (it.end ? location.pathname === it.to : location.pathname.startsWith(it.to))),
      )
      .map((g) => g.id),
  );
  foldedGroups.zapamtiAktivne(activeGroupIds);

  const initials = `${state.user.firstName?.[0] ?? ""}${state.user.lastName?.[0] ?? ""}`.toUpperCase() || "—";
  const company = state.user.companyName ?? (isInternal ? "Bankom · Interno" : "");

  return (
    <ChatContext.Provider value={chat}>
    <div className={`app${samoIkone ? " nav-collapsed" : ""}${mobileNavOpen ? " mobile-nav-open" : ""}`}>
      <div className="mobile-nav-backdrop" onClick={() => setMobileNavOpen(false)} aria-hidden="true" />
      <aside className="sidebar">
        <Link to="/dashboard" className="brand" style={{ textDecoration: "none" }}>
          <span className="brand-mark" style={{ overflow: "hidden", padding: 3, background: "var(--surface)" }}>
            {/* Just the leaf mark (favicon), not the full wordmark logo: the "Bankom"
                text sits right next to it, so the leaf fills the chip and reads clearly. */}
            <img src="/favicon.svg" alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          </span>
          <span className="brand-text">
            <b>Bankom</b>
            <span>{t("app.tagline")}</span>
          </span>
        </Link>

        <button
          type="button"
          className="nav-toggle"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={t("nav.toggleMenu", "Toggle menu")}
          title={t("nav.toggleMenu", "Toggle menu")}
        >
          <Ic.arrowRight />
        </button>

        <nav className="nav">
          {/* Izlaz iz stanja u kom je korisnik sklopio sve i ostao bez menija. Stoji samo kad ima sta
              da se otvori, pa ne zauzima red kad nije potreban. */}
          {!samoIkone && foldedGroups.koliko > 0 ? (
            <button type="button" className="nav-open-all" onClick={foldedGroups.otvoriSve}>
              {t("nav.expandAll", "Expand all")}
            </button>
          ) : null}
          {visibleGroups.map((group) => {
            // Sklapa se samo grupa koja time nesto dobija. Grupa sa jednom ili dve stavke sakrivanjem
            // ne stedi ni jedan red koji se vidi, a nosi strelicu koja poziva na klik — i onda meni ume
            // da zavrsi kao deset sivih natpisa bez ijedne stavke. Prag je tri.
            const mozeDaSeSklopi = group.items.length >= 3;
            // U uskom meniju (samo ikone) sklapanje se ne primenjuje: zaglavlja su sakrivena, pa bi
            // sklopljena grupa nestala bez ijednog mesta na koje se klikne da se vrati.
            // `open` je tacno korisnikov izbor, pa `aria-expanded` govori istinu i nijedno zaglavlje nije
            // mrtvo dugme. Grupu sa otvorenim ekranom otklapa ucinak u kuki, pri promeni rute.
            const open = samoIkone || !mozeDaSeSklopi || !foldedGroups.isFolded(group.id);
            return (
              <div key={group.id} className="nav-group">
                {mozeDaSeSklopi ? (
                  <button
                    type="button"
                    className="nav-section nav-section-btn"
                    aria-expanded={open}
                    aria-controls={`nav-group-${group.id}`}
                    onClick={() => foldedGroups.toggle(group.id)}
                  >
                    <span>{group.title}</span>
                    <Ic.chevron />
                  </button>
                ) : (
                  <div className="nav-section">{group.title}</div>
                )}
                <div id={`nav-group-${group.id}`} hidden={!open}>
                  {group.items.map((it, i) => (
                    <div key={it.to}>
                      {/* Podnaslov se ispisuje kad se promeni, pa podnaslov cije su sve stavke
                          sklonjene dozvolama nestaje zajedno sa njima. */}
                      {it.section && it.section !== group.items[i - 1]?.section ? (
                        <div className="nav-subsection">{it.section}</div>
                      ) : null}
                      {it.disabled ? (
                        <DisabledNavItem label={it.label} icon={<it.icon />} />
                      ) : (
                        <NavItem to={it.to} label={it.label} icon={<it.icon />} end={it.end} badge={it.badge} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="sidebar-foot">
          {state.user.avatarUrl ? (
            <img
              src={state.user.avatarUrl}
              alt=""
              className="user-avatar"
              style={{ padding: 0, objectFit: "cover", border: "1px solid var(--border)" }}
            />
          ) : (
            <span className="user-avatar">{initials}</span>
          )}
          <div className="user-meta">
            <b>{state.user.firstName} {state.user.lastName}</b>
            <span>{company}</span>
          </div>
          <Link to="/profile" className="icon-btn" title={t("nav.profile")} style={{ display: "grid", placeItems: "center", padding: 6, color: "var(--ink-3)" }}>
            <Ic.user />
          </Link>
        </div>
      </aside>

      <header className="topbar">
        <button
          type="button"
          className="mobile-nav-btn"
          onClick={() => setMobileNavOpen((o) => !o)}
          aria-label={t("nav.toggleMenu", "Toggle menu")}
          aria-expanded={mobileNavOpen}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <div className="topbar-search" style={{ flex: 1, maxWidth: 480 }}>
          <GlobalSearch />
        </div>
        <div className="topbar-actions" style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
          <ThemeToggle />
          <NotificationBell />
          <LocaleSwitcher />
          <button type="button" onClick={handleLogout} title={t("nav.logout")} style={logoutBtnStyle}>
            {t("nav.logout")}
          </button>
        </div>
      </header>

      <main className="app-main">
        {/* Suspended-company notice: the customer can browse, but
            ordering is blocked server-side — tell them why up front. */}
        {isCustomer && state.user.companySuspended && (
          <div role="alert" className="tag warn" style={{ display: "block", padding: "10px 14px", marginBottom: 14, fontSize: 13, borderRadius: "var(--r-md)" }}>
            {t("orders.suspendedBanner", "Your account is temporarily blocked for ordering. Please contact your Bankom sales representative.")}
          </div>
        )}
        <Outlet />
      </main>

      {/* The chat has its own writing box in the corner the assistant's button would cover. */}
      {!location.pathname.startsWith("/chat") && <AssistantChat />}
    </div>
    </ChatContext.Provider>
  );
}

function NavItem({
  to, label, icon, end, badge,
}: { to: string; label: string; icon: ReactNode; end?: boolean; badge?: number }) {
  return (
    <NavLink
      to={to}
      end={end}
      title={label}
      className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
      style={{ cursor: "pointer" }}
    >
      {icon}
      <span style={{ flex: 1 }}>{label}</span>
      {badge !== undefined && badge > 0 && (
        <span
          aria-label={`${badge} pending`}
          style={{
            background: "var(--brand-primary)",
            color: "var(--on-primary)",
            fontSize: 10,
            fontWeight: 700,
            padding: "1px 7px",
            borderRadius: 999,
            minWidth: 18,
            textAlign: "center",
            lineHeight: 1.5,
          }}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </NavLink>
  );
}

/** A nav row for a module that has no route yet.
 * Shown so the two-sided nav structure is visible ahead of time, without
 * linking anywhere. */
function DisabledNavItem({ label, icon }: { label: string; icon: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div
      className="nav-item"
      aria-disabled="true"
      title={label}
      style={{ cursor: "not-allowed", opacity: 0.55 }}
    >
      {icon}
      <span style={{ flex: 1 }}>{label}</span>
      <span className="badge">{t("nav.comingSoon", "Soon")}</span>
    </div>
  );
}

const logoutBtnStyle: React.CSSProperties = {
  padding: "6px 12px",
  background: "transparent",
  border: "1px solid var(--border)",
  borderRadius: 6,
  color: "var(--ink-2)",
  fontSize: 13,
  cursor: "pointer",
  whiteSpace: "nowrap",
  flex: "none",
};
